import { ApiError } from '../../services/api';
import type {
  OfferDiscountType,
  OfferResponse,
  OfferStatus,
  OfferUpsertRequest,
} from '../../types/offers';
import type { OfferingV2Response } from '../../types/offeringsV2';
import type { OffersT } from './offersI18n';

// ── Limits (requirements §6 BR-4..BR-8, §4 Limits) ──
export const TITLE_MAX = 60;
export const SUBTITLE_MAX = 90;
export const MAX_WINDOW_DAYS = 90;
export const PERCENT_MIN = 5;
export const PERCENT_MAX = 70;
export const FIXED_MIN = 5;
export const MAX_PER_BOOKING_MIN = 5;
export const MAX_BOOKINGS_MIN = 1;
export const MAX_BOOKINGS_MAX = 10000;

/** Statuses whose content edits become a pending revision (BR-12). */
export const RUNNING_STATUSES: OfferStatus[] = ['SCHEDULED', 'LIVE', 'PAUSED'];
/** Statuses the partner edits in place and (re)submits. */
export const EDITABLE_STATUSES: OfferStatus[] = ['DRAFT', 'REJECTED', 'SUSPENDED'];

// ── Dates (Asia/Dubai, ISO yyyy-MM-dd) ──
export function todayInDubai(now: Date = new Date()): string {
  // en-CA formats as yyyy-MM-dd.
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Dubai', year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
}

function isoToUtcMs(iso: string): number {
  const [y, m, d] = iso.split('-').map(Number);
  return Date.UTC(y, (m || 1) - 1, d || 1);
}

export function addDays(iso: string, days: number): string {
  return new Date(isoToUtcMs(iso) + days * 86400000).toISOString().slice(0, 10);
}

export function daysBetween(fromIso: string, toIso: string): number {
  return Math.round((isoToUtcMs(toIso) - isoToUtcMs(fromIso)) / 86400000);
}

export function formatDate(iso: string | null | undefined, lang: 'en' | 'ar' = 'en'): string {
  if (!iso) return '—';
  const d = new Date(isoToUtcMs(iso.slice(0, 10)));
  return d.toLocaleDateString(lang === 'ar' ? 'ar-AE' : 'en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
}

/** An ISO instant shown as its Asia/Dubai calendar day ("13 Oct 2026"); '—' when missing or unparsable. */
export function formatInstantDate(iso: string | null | undefined, lang: 'en' | 'ar' = 'en'): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return formatDate(todayInDubai(d), lang);
}

// ── Money ──
export function formatAed(n: number | null | undefined): string {
  const v = n ?? 0;
  return `AED ${v.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

type PricedOffering = Pick<OfferingV2Response, 'flatPrice' | 'priceRange'>;

/**
 * The offering's "from" price, with the server's precedence (OfferingResponseMapper#publicOffer,
 * OfferingQuoteService): the cheapest active variant (`priceRange.min`) when the service is priced
 * per variant, else the flat price. A stale `flatPrice` left on a variant-priced service never wins —
 * the customer app, the offer validator and booking all price on the variant minimum.
 */
export function offeringFromPrice(o: PricedOffering): number | null {
  return o.priceRange?.min ?? o.flatPrice ?? null;
}

/** True when the service's active variants span more than one price (show "from AED X"). */
export function offeringHasPriceRange(o: PricedOffering): boolean {
  const r = o.priceRange;
  return r != null && r.min != null && r.max != null && r.max !== r.min;
}

/** Lowest price an offer may leave on a service; below it checkout skips the service (BR-6). */
export const MIN_RESULTING_PRICE = 1;

const round2 = (n: number) => Math.round(n * 100) / 100;

/**
 * Client-side preview only — the server computes the authoritative price. Mirrors
 * OfferPricingService.applyRule: PERCENT rounded to 2 dp then capped, FIXED is the amount, and a
 * discount that would leave under AED 1 leaves the list price (the service is skipped, not free).
 */
export function previewDiscountedPrice(
  listPrice: number,
  type: OfferDiscountType,
  value: number,
  maxPerBooking: number | null,
): number {
  if (!Number.isFinite(listPrice) || listPrice <= 0 || !Number.isFinite(value) || value <= 0) return listPrice;
  const list = round2(listPrice);
  let discount = type === 'PERCENT' ? round2((list * value) / 100) : value;
  if (type === 'PERCENT' && maxPerBooking != null && maxPerBooking > 0) discount = Math.min(discount, maxPerBooking);
  discount = round2(Math.min(discount, list));
  if (list - discount < MIN_RESULTING_PRICE) return list;
  return round2(list - discount);
}

interface OrderedOfferItem {
  offeringId: string;
  name: string;
  listPrice: number;
  discountedPrice: number;
  bookable?: boolean;
}

/** Plain code-unit order, like Java's String.compareTo on the server. */
const cmp = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0);

/**
 * The server's item order (OfferResponseMapper.ITEM_ORDER, the same as the app feed): bookable first,
 * the largest saving, then name, then id. `items[0]` is then the lead the customer sees.
 */
export function sortOfferItems<T extends OrderedOfferItem>(items: T[]): T[] {
  const saving = (i: T) => round2((i.listPrice ?? 0) - (i.discountedPrice ?? i.listPrice ?? 0));
  return [...items].sort((a, b) =>
    Number(a.bookable === false) - Number(b.bookable === false)
    || saving(b) - saving(a)
    || cmp(a.name ?? '', b.name ?? '')
    || cmp(a.offeringId, b.offeringId));
}

export function badgeText(type: OfferDiscountType, value: number, lang: 'en' | 'ar'): string {
  const v = Number.isFinite(value) ? value.toLocaleString('en-US', { maximumFractionDigits: 2 }) : '0';
  if (type === 'PERCENT') return lang === 'ar' ? `خصم ${v}٪` : `${v}% OFF`;
  return lang === 'ar' ? `خصم ${v} درهم` : `AED ${v} OFF`;
}

// ── Contact details (BR-8). Server is authoritative; this is an early hint. ──
const EMAIL_RE = /[^\s@]+@[^\s@]+\.[^\s@]+/;
const URL_RE = /(https?:\/\/|www\.)\S+|\b[\w-]+\.(com|ae|net|org|io|co|me|info|biz)\b/i;
// 9+ digits with optional single separators: UAE landlines have 9, mobiles 10; dates (8) pass.
const PHONE_RE = /(?:\+|00)?(?:[0-9٠-٩][\s-]?){9,}/;
export function hasContactDetails(text: string | null | undefined): boolean {
  if (!text) return false;
  return EMAIL_RE.test(text) || URL_RE.test(text) || PHONE_RE.test(text);
}

// ── Form model ──
/** Spec 073: one checked service and its own discount value (as typed). */
export interface OfferFormItem {
  offeringId: string;
  discountValue: string;
}

export interface OfferForm {
  titleEn: string;
  titleAr: string;
  subtitleEn: string;
  subtitleAr: string;
  startsAt: string;
  endsAt: string;
  discountType: OfferDiscountType;
  /**
   * Spec 073: the "Default discount" — UI only, never sent. Pre-fills newly checked services and is
   * what "Apply to all" copies. Each service's real value lives on `items`.
   */
  defaultDiscount: string;
  maxDiscountPerBooking: string;
  maxBookings: string;
  /** Checked services in the order they were picked, each with its own value. */
  items: OfferFormItem[];
  bannerStorageKey: string | null;
  bannerUrl: string | null;
  bannerArStorageKey: string | null;
  bannerArUrl: string | null;
}

type StaticOfferField =
  | 'titleEn' | 'titleAr' | 'subtitleEn' | 'subtitleAr'
  | 'startsAt' | 'endsAt'
  | 'discountValue' | 'maxDiscountPerBooking' | 'maxBookings' | 'offeringIds'
  | 'banner' | 'bannerAr';

/** Spec 073: a per-service value error is keyed `item:<offeringId>` and shown under that row. */
export type ItemField = `item:${string}`;
export type OfferField = StaticOfferField | ItemField;

export const itemField = (offeringId: string): ItemField => `item:${offeringId}`;
export const isItemField = (f: string): f is ItemField => f.startsWith('item:');

export type OfferFieldErrors = Partial<Record<OfferField, string>>;

/** Wizard step that owns each field (0 Details · 1 Services & discount · 2 Banner · 3 Preview). */
export const FIELD_STEP: Record<StaticOfferField, number> = {
  titleEn: 0, titleAr: 0, subtitleEn: 0, subtitleAr: 0, startsAt: 0, endsAt: 0,
  discountValue: 1, maxDiscountPerBooking: 1, maxBookings: 1, offeringIds: 1,
  banner: 2, bannerAr: 2,
};

export function fieldStep(f: OfferField): number {
  return isItemField(f) ? 1 : FIELD_STEP[f];
}

export function emptyForm(today: string = todayInDubai()): OfferForm {
  return {
    titleEn: '', titleAr: '', subtitleEn: '', subtitleAr: '',
    startsAt: today,
    endsAt: addDays(today, 30),
    discountType: 'PERCENT',
    defaultDiscount: '10',
    maxDiscountPerBooking: '',
    maxBookings: '',
    items: [],
    bannerStorageKey: null, bannerUrl: null,
    bannerArStorageKey: null, bannerArUrl: null,
  };
}

const numStr = (n: number | null | undefined) => (n == null ? '' : String(n));

export function formFromOffer(o: OfferResponse): OfferForm {
  // Spec 073: a row without its own value stays blank (no fallback to the offer value); submit flags it.
  const items = (o.items ?? []).map((i) => ({ offeringId: i.offeringId, value: i.discountValue ?? null }));
  const values = items.map((i) => i.value).filter((v): v is number => v != null && Number.isFinite(v));
  // The default starts from the highest service value (design §2); else the offer value.
  const defaultValue = values.length ? Math.max(...values) : o.discountValue;
  return {
    titleEn: o.titleEn ?? '',
    titleAr: o.titleAr ?? '',
    subtitleEn: o.subtitleEn ?? '',
    subtitleAr: o.subtitleAr ?? '',
    startsAt: (o.startsAt ?? '').slice(0, 10),
    endsAt: (o.endsAt ?? '').slice(0, 10),
    discountType: o.discountType ?? 'PERCENT',
    defaultDiscount: numStr(defaultValue),
    maxDiscountPerBooking: numStr(o.maxDiscountPerBooking),
    maxBookings: numStr(o.maxBookings),
    items: items.map((i) => ({ offeringId: i.offeringId, discountValue: numStr(i.value) })),
    bannerStorageKey: o.banner?.storageKey ?? null,
    bannerUrl: o.banner?.downloadUrl ?? null,
    bannerArStorageKey: o.bannerAr?.storageKey ?? null,
    bannerArUrl: o.bannerAr?.downloadUrl ?? null,
  };
}

const optText = (s: string) => (s.trim() ? s.trim() : null);
const optNum = (s: string) => (s.trim() === '' ? null : Number(s));

/** Spec 073: the typed per-service values, as numbers (null when empty or not a number). */
export function itemValues(f: Pick<OfferForm, 'items'>): (number | null)[] {
  return f.items.map((i) => {
    const n = optNum(i.discountValue);
    return n != null && Number.isFinite(n) ? n : null;
  });
}

/** Lowest typed per-service value (what the server stores as the offer value); null when none. */
export function minItemValue(f: Pick<OfferForm, 'items'>): number | null {
  const v = itemValues(f).filter((n): n is number => n != null);
  return v.length ? Math.min(...v) : null;
}

/** Highest typed per-service value; null when none. */
export function maxItemValue(f: Pick<OfferForm, 'items'>): number | null {
  const v = itemValues(f).filter((n): n is number => n != null);
  return v.length ? Math.max(...v) : null;
}

/** True when the checked services carry more than one distinct value ("Up to" badge). */
export function itemValuesDiffer(f: Pick<OfferForm, 'items'>): boolean {
  return new Set(itemValues(f).filter((n): n is number => n != null)).size > 1;
}

/** Spec 073 "Apply to all": copies the default discount to every checked service. */
export function applyDefaultToAll(f: OfferForm): OfferForm {
  return { ...f, items: f.items.map((i) => ({ ...i, discountValue: f.defaultDiscount })) };
}

/** Checks or unchecks a service; a newly checked one is pre-filled with the default discount. */
export function toggleItem(f: OfferForm, offeringId: string): OfferForm {
  const on = f.items.some((i) => i.offeringId === offeringId);
  return {
    ...f,
    items: on
      ? f.items.filter((i) => i.offeringId !== offeringId)
      : [...f.items, { offeringId, discountValue: f.defaultDiscount }],
  };
}

export function formToRequest(f: OfferForm, version?: number): OfferUpsertRequest {
  const values = itemValues(f);
  const req: OfferUpsertRequest = {
    titleEn: f.titleEn.trim(),
    titleAr: optText(f.titleAr),
    subtitleEn: optText(f.subtitleEn),
    subtitleAr: optText(f.subtitleAr),
    startsAt: f.startsAt,
    endsAt: f.endsAt,
    discountType: f.discountType,
    // The server recomputes it as MIN(items); with no service yet it keeps the default.
    discountValue: minItemValue(f) ?? optNum(f.defaultDiscount),
    maxDiscountPerBooking: f.discountType === 'PERCENT' ? optNum(f.maxDiscountPerBooking) : null,
    maxBookings: optNum(f.maxBookings),
    offeringIds: f.items.map((i) => i.offeringId),
    items: f.items.map((i, idx) => ({ offeringId: i.offeringId, discountValue: values[idx] })),
    bannerStorageKey: f.bannerStorageKey,
    bannerArStorageKey: f.bannerArStorageKey,
  };
  if (version != null) req.version = version;
  return req;
}

export interface ValidationContext {
  today: string;
  /** Skip the "start ≥ today" rule (offer already started, start date locked). */
  startLocked: boolean;
  /** Spec 073: a service's "from" list price (a FIXED value must stay at least AED 1 below it). */
  priceOf: (offeringId: string) => number | null;
  bookingsUsed: number;
}

/** Client-side validation of one wizard step. Returns translation keys per field. */
export function validateStep(step: number, f: OfferForm, ctx: ValidationContext): OfferFieldErrors {
  const e: OfferFieldErrors = {};
  if (step === 0) {
    if (!f.titleEn.trim()) e.titleEn = 'offers.err.titleRequired';
    else if (f.titleEn.trim().length > TITLE_MAX) e.titleEn = 'offers.err.titleTooLong';
    else if (hasContactDetails(f.titleEn)) e.titleEn = 'offers.err.contactDetails';
    if (f.titleAr.trim().length > TITLE_MAX) e.titleAr = 'offers.err.titleTooLong';
    else if (hasContactDetails(f.titleAr)) e.titleAr = 'offers.err.contactDetails';
    if (f.subtitleEn.trim().length > SUBTITLE_MAX) e.subtitleEn = 'offers.err.subtitleTooLong';
    else if (hasContactDetails(f.subtitleEn)) e.subtitleEn = 'offers.err.contactDetails';
    if (f.subtitleAr.trim().length > SUBTITLE_MAX) e.subtitleAr = 'offers.err.subtitleTooLong';
    else if (hasContactDetails(f.subtitleAr)) e.subtitleAr = 'offers.err.contactDetails';
    if (!f.startsAt) e.startsAt = 'offers.err.startRequired';
    else if (!ctx.startLocked && f.startsAt < ctx.today) e.startsAt = 'offers.err.startPast';
    if (!f.endsAt) e.endsAt = 'offers.err.endRequired';
    else if (f.startsAt && f.endsAt <= f.startsAt) e.endsAt = 'offers.err.endBeforeStart';
    else if (f.startsAt && daysBetween(f.startsAt, f.endsAt) > MAX_WINDOW_DAYS) e.endsAt = 'offers.err.window';
  }
  if (step === 1) {
    if (f.items.length === 0) e.offeringIds = 'offers.err.noService';
    // Spec 073: every checked service needs its own value in range (R2, R4).
    for (const item of f.items) {
      const raw = item.discountValue.trim();
      const v = Number(raw);
      const key = itemField(item.offeringId);
      if (f.discountType === 'PERCENT') {
        if (raw === '' || !Number.isFinite(v) || v < PERCENT_MIN || v > PERCENT_MAX) e[key] = 'offers.err.percentRange';
      } else if (raw === '' || !Number.isFinite(v) || v < FIXED_MIN) {
        e[key] = 'offers.err.fixedMin';
      } else {
        const price = ctx.priceOf(item.offeringId);
        if (price != null && v > price - MIN_RESULTING_PRICE) e[key] = 'offers.err.exceedsServicePrice';
      }
    }
    if (f.discountType === 'PERCENT' && f.maxDiscountPerBooking.trim() !== '') {
      const m = Number(f.maxDiscountPerBooking);
      if (!Number.isFinite(m) || m < MAX_PER_BOOKING_MIN) e.maxDiscountPerBooking = 'offers.err.maxPerBookingMin';
    }
    if (f.maxBookings.trim() !== '') {
      const mb = Number(f.maxBookings);
      if (!Number.isInteger(mb) || mb < MAX_BOOKINGS_MIN || mb > MAX_BOOKINGS_MAX) e.maxBookings = 'offers.err.maxBookingsRange';
      else if (mb < ctx.bookingsUsed) e.maxBookings = 'offers.err.maxBelowUsed';
    }
  }
  if (step === 2) {
    if (!f.bannerStorageKey) e.banner = 'offers.err.bannerRequired';
  }
  return e;
}

export function validateAll(f: OfferForm, ctx: ValidationContext): OfferFieldErrors {
  return { ...validateStep(0, f, ctx), ...validateStep(1, f, ctx), ...validateStep(2, f, ctx) };
}

export function firstErrorStep(errors: OfferFieldErrors): number | null {
  const steps = (Object.keys(errors) as OfferField[]).map(fieldStep);
  return steps.length ? Math.min(...steps) : null;
}

// ── API error mapping (design.md §3 error codes) ──
const CODE_TO_FIELD: Record<string, { field: OfferField; key: string }> = {
  OFFER_WINDOW_INVALID: { field: 'endsAt', key: 'offers.err.window' },
  DISCOUNT_OUT_OF_RANGE: { field: 'discountValue', key: 'offers.err.percentRange' },
  DISCOUNT_EXCEEDS_PRICE: { field: 'discountValue', key: 'offers.err.exceedsPrice' },
  NO_ACTIVE_SERVICE: { field: 'offeringIds', key: 'offers.err.noService' },
  BANNER_REQUIRED: { field: 'banner', key: 'offers.err.bannerRequired' },
  MAX_BOOKINGS_BELOW_USED: { field: 'maxBookings', key: 'offers.err.maxBelowUsed' },
  INVALID_STORAGE_KEY: { field: 'banner', key: 'offers.err.storageKey' },
  CONTACT_DETAILS_FORBIDDEN: { field: 'titleEn', key: 'offers.err.contactDetails' },
  ARABIC_REQUIRED: { field: 'titleAr', key: 'offers.err.arabicRequired' },
};

const CODE_TO_FORM: Record<string, string> = {
  GARAGE_BLOCKED: 'offers.err.garageBlocked',
  INVALID_OFFER_TRANSITION: 'offers.err.transition',
  OFFER_STATE_CHANGED: 'offers.err.stateChanged',
  OFFER_ENDED: 'offers.err.ended',
  FEATURE_DISABLED: 'offers.err.featureDisabled',
};

/** Server field names → form fields. */
const SERVER_FIELD: Record<string, OfferField> = {
  titleEn: 'titleEn', titleAr: 'titleAr', subtitleEn: 'subtitleEn', subtitleAr: 'subtitleAr',
  startsAt: 'startsAt', endsAt: 'endsAt',
  discountType: 'discountValue', discountValue: 'discountValue',
  maxDiscountPerBooking: 'maxDiscountPerBooking', maxBookings: 'maxBookings',
  offeringIds: 'offeringIds', bannerStorageKey: 'banner', bannerArStorageKey: 'bannerAr',
};

export interface MappedOfferError {
  fieldErrors: OfferFieldErrors;
  formError: string | null;
  /** The resolved backend code, when there was one. */
  code: string | null;
}

/** Spec 073: `items[2].discountValue` → the request's item index (2). */
const ITEM_FIELD_RE = /^items\[(\d+)]\.discountValue$/;

/** Per-row message for a per-service code (the row names the service, so the text is about "this service"). */
const ITEM_CODE_KEY: Record<string, string> = {
  DISCOUNT_OUT_OF_RANGE: 'offers.err.percentRange',
  DISCOUNT_EXCEEDS_PRICE: 'offers.err.exceedsServicePrice',
};

/**
 * Maps an API failure to translated field errors and/or one form-level message.
 * The code is read from the body's `code`, then `error`, then any field-error value
 * that is itself a known code (e.g. `errors: { titleAr: "CONTACT_DETAILS_FORBIDDEN" }`).
 * Spec 073: `items[<i>].discountValue` errors land on the row of `itemIds[i]` (the request's item order).
 */
export function mapOfferError(err: unknown, t: OffersT, itemIds: string[] = []): MappedOfferError {
  const out: MappedOfferError = { fieldErrors: {}, formError: null, code: null };
  if (!(err instanceof ApiError)) {
    out.formError = t('offers.err.generic');
    return out;
  }
  const known = (c: string | null | undefined) => !!c && (c in CODE_TO_FIELD || c in CODE_TO_FORM);
  let code: string | null = known(err.errorCode) ? err.errorCode : known(err.code) ? err.code : null;

  // Field-level errors from the body.
  for (const [serverField, value] of Object.entries(err.fieldErrors ?? {})) {
    const valueCode = typeof value === 'string' && known(value) ? value : null;
    if (valueCode && !code) code = valueCode;
    const itemMatch = ITEM_FIELD_RE.exec(serverField);
    if (itemMatch) {
      const id = itemIds[Number(itemMatch[1])];
      const key = valueCode ? ITEM_CODE_KEY[valueCode] : undefined;
      const text = key ? t(key) : (typeof value === 'string' && value ? value : t('offers.err.generic'));
      out.fieldErrors[id ? itemField(id) : 'offeringIds'] = text;
      continue;
    }
    const field = SERVER_FIELD[serverField];
    if (!field) continue;
    if (valueCode && CODE_TO_FIELD[valueCode]) out.fieldErrors[field] = t(CODE_TO_FIELD[valueCode].key);
    else if (typeof value === 'string' && value) out.fieldErrors[field] = valueCode && CODE_TO_FORM[valueCode] ? t(CODE_TO_FORM[valueCode]) : value;
  }

  out.code = code ?? err.errorCode ?? err.code ?? null;
  if (code && CODE_TO_FIELD[code]) {
    const { field, key } = CODE_TO_FIELD[code];
    // CONTACT_DETAILS_FORBIDDEN may name its field in `errors`; only default to titleEn when it doesn't.
    const alreadyPlaced = Object.keys(out.fieldErrors).length > 0;
    if (!alreadyPlaced) out.fieldErrors[field] = t(key);
  } else if (code && CODE_TO_FORM[code]) {
    out.formError = t(CODE_TO_FORM[code]);
  } else if (Object.keys(out.fieldErrors).length === 0) {
    out.formError = err.status === 503 ? t('offers.err.featureDisabled') : (err.message || t('offers.err.generic'));
  }
  return out;
}

/** Message for list-page actions (no fields there). */
export function offerActionErrorMessage(err: unknown, t: OffersT): string {
  const m = mapOfferError(err, t);
  if (m.formError) return m.formError;
  const first = Object.values(m.fieldErrors)[0];
  return first ?? t('offers.err.generic');
}

// ── List page helpers ──
export type RowAction = 'pause' | 'resume' | 'end' | 'archive' | 'withdraw';

export function statusChipClass(status: OfferStatus): string {
  return `offer-status offer-status--${status.toLowerCase().replace('_', '-')}`;
}

/** Which row actions an offer allows (BR-9 transitions + archive rule). */
export function rowActionsFor(o: OfferResponse, archived = false): { edit: boolean; actions: RowAction[] } {
  if (archived || o.hiddenAt) return { edit: false, actions: [] };
  const revisionPending = o.pendingRevision?.status === 'PENDING_APPROVAL';
  switch (o.status) {
    case 'DRAFT':
    case 'REJECTED':
      return { edit: true, actions: ['archive'] };
    case 'SUSPENDED':
      return { edit: true, actions: [] };
    case 'PENDING_APPROVAL':
      return { edit: false, actions: ['withdraw'] };
    case 'SCHEDULED':
      return { edit: true, actions: [...(revisionPending ? ['withdraw' as const] : []), 'end'] };
    case 'LIVE':
      return { edit: true, actions: [...(revisionPending ? ['withdraw' as const] : []), 'pause', 'end'] };
    case 'PAUSED':
      return { edit: true, actions: [...(revisionPending ? ['withdraw' as const] : []), 'resume', 'end'] };
    case 'ENDED':
      return { edit: false, actions: ['archive'] };
    default:
      return { edit: false, actions: [] };
  }
}

